// Payroll — the Payroll Dashboard (owner instruction, 2026-08-15, verbatim
// intent: the page shows the previous submitted payroll — currently Week 33
// — as its default, with a pull-down of every archived week; the weekly
// entry form is a separate menu item INSIDE this page, not a separate route).
//
// TWO GATES, deliberately different mechanisms:
//
//   1. WHO SEES THE SUB-NAV AT ALL — decided here, client-side, from
//      `session.value.me.role`. A manager gets no "Dashboard" tab and no
//      week picker; the entry form is the only thing on the page. This is a
//      display decision, not the access control — see #2.
//
//   2. WHO THE SERVER LETS READ THE ARCHIVE — `GET
//      /api/v1/payroll/review-documents(/:id)` is admin-only for real
//      (`rule: 'payroll_archive_admin_only'`, a 403 to anyone else). The
//      role check above keeps a manager from ever asking, but the refusal
//      still renders as its own named FaultState if the server ever
//      disagrees with the client's idea of who is signed in (INV-9): a
//      locked archive and an empty one must never read the same.
//
// Tomer's ruling, 2026-09-07: the Dashboard shows the reviewed payroll page
// for the selected location and week — nothing else. The older Phase 7
// store-backed register/extras section ("Current entries") and the roster
// placeholder are gone for good; manager entries live under the Manager
// entry tab. `payrollFault`/`isAdminOnlyRefusal` (from bootstrap's `payroll`
// slice) still gate the whole Dashboard body below, so R-5/INV-9 keep
// rendering as their own named states even with the older section removed.
import type { JSX } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { EstBonus } from '../components/EstBonus';
import { snapshot } from '../lib/bootstrap';
import { session } from '../lib/session';
import { authedGet } from '../lib/apiClient';
import { useResource } from '../lib/store';
import { type LocationCode } from '../lib/locations';
import { ClinicTabs, visibleClinics, activeClinicFrom } from '../components/ClinicTabs';
import { FaultState, EmptyState } from '../components/FaultState';
import { ManagerEntry } from '../components/ManagerEntry';
import { FavoriteStar } from '../components/FavoriteStar';
import { PayrollWeekView, type PayrollWeekResponse } from '../components/PayrollWeekView';

// --- the review-document archive (GET /api/v1/payroll/review-documents) ----

export interface PayrollReviewDocumentSummary {
  id: number;
  adpWeek: number;
  year: number;
  periodStart: string | null;
  periodEnd: string | null;
  payDate: string | null;
  title: string;
  byteLength: number;
  importedAt: string;
  // 062: null = all locations. A per-location document carries its clinic's
  // code (MEWP/MEVL/MEWG/MELM).
  locationCode: LocationCode | null;
  // 062: PAGE = the rendered week page; BOARD = the archived review board
  // ("Open the reviewed board"). A week can carry both plus per-location
  // PAGEs, all as separate rows here. 079: VALIDATION = the engine's weekly
  // validation report, one per clinic (owner, 2026-09-24).
  // 105: COMPARE = payroll-to-payroll comparison, one per clinic (owner, 2026-10-20).
  kind: 'PAGE' | 'BOARD' | 'VALIDATION' | 'COMPARE';
}

export interface PayrollReviewDocumentDetail {
  id: number;
  adpWeek: number;
  year: number;
  periodStart: string | null;
  periodEnd: string | null;
  payDate: string | null;
  title: string;
  html: string;
}

const REVIEW_DOCUMENTS_KEY = 'payroll-review-documents';

/** "Week 33 — 07/26/2026 – 08/08/2026 (paid 08/13/2026)", falling back to
 * "Week 11 — paid 03/12/2026" when the period dates are not on the document
 * (older imports may only carry a pay date). Dates render on the UTC
 * calendar day the server sent, matching how they were stored — this is a
 * label for a document, not a clinic-local timestamp. */
export function documentWeekLabel(doc: PayrollReviewDocumentSummary): string {
  const fmt = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getUTCMonth() + 1)}/${pad(d.getUTCDate())}/${d.getUTCFullYear()}`;
  };
  const paid = doc.payDate ? ` (paid ${fmt(doc.payDate)})` : '';
  if (doc.periodStart && doc.periodEnd) {
    return `Week ${doc.adpWeek} — ${fmt(doc.periodStart)} – ${fmt(doc.periodEnd)}${paid}`;
  }
  return doc.payDate ? `Week ${doc.adpWeek} — paid ${fmt(doc.payDate)}` : `Week ${doc.adpWeek}`;
}

export function PayrollPage(): JSX.Element {
  const role = session.value.me?.role;
  // A manager's weekly entry form lives on this page (src/manager_entry.js);
  // the archive above it does not, and never has for a manager
  // (`checkPayrollArchiveAccess`, R-5). Deciding that here from the role we
  // already have avoids asking the server for a dashboard nobody mapped as
  // MANAGER is allowed to see, and means a manager never has to look at an
  // empty or refused dashboard to reach the one thing that is theirs.
  const isManager = role === 'MANAGER';

  // A hire that starts on the Recruiting board lands on the Entry tab with
  // the New hire line prefilled (?candidate=<id>, read by ManagerEntry).
  const [subTab, setSubTab] = useState<'dashboard' | 'entry' | 'est'>(
    () => {
      const q = new URLSearchParams(window.location.search);
      if (q.has('candidate')) return 'entry';
      // A What's New link may open a tab directly (owner, 2026-10-06).
      if (q.get('tab') === 'est') return 'est';
      if (q.get('tab') === 'entry') return 'entry';
      return 'dashboard';
    },
  );
  const clinics = visibleClinics();
  const [selectedClinic, setSelectedClinic] = useState<LocationCode | null>(null);
  const activeClinic = activeClinicFrom(selectedClinic, clinics);
  // Lifted out of PayrollDashboard so the native Week view's "Open the
  // reviewed board" link can hand the archive a document id and switch tabs
  // to it in one click, rather than the two surfaces keeping separate,
  // unreachable ideas of which document is selected.
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);

  // EST bonus (owner, 2026-10-05): only for a clinic on the esthetician bonus
  // plan (The Villages, Winter Garden) — src/est_bonus_calc.js EST_BONUS_PLANS.
  // Owner, 2026-10-05: "each location needs to have this just lake mary and
  // winter park will be empty" — the tab is on every clinic; EstBonus itself
  // shows an empty tab for a clinic not on the plan.
  const hasEstBonus = Boolean(activeClinic);
  const estTab = (
    <button
      type="button" role="tab"
      aria-selected={subTab === 'est'}
      aria-current={subTab === 'est' ? 'page' : undefined}
      class={subTab === 'est' ? 'tab active' : 'tab'}
      onClick={() => setSubTab('est')}
      data-testid="payroll-subnav-est"
    >
      EST bonus
    </button>
  );

  if (isManager) {
    return (
      <div class="main">
        <div class="page-header">
          <div class="section-head-row">
            <h1>Payroll Dashboard</h1>
            <div class="section-head-actions">
              <FavoriteStar itemKey="payroll.dashboard" />
            </div>
          </div>
        </div>
        {hasEstBonus ? (
          <div class="cadence-tabs" role="tablist" aria-label="Payroll section">
            <button
              type="button" role="tab"
              aria-selected={subTab !== 'est'}
              aria-current={subTab !== 'est' ? 'page' : undefined}
              class={subTab !== 'est' ? 'tab active' : 'tab'}
              onClick={() => setSubTab('entry')}
              data-testid="payroll-subnav-entry"
            >
              Manager entry
            </button>
            {estTab}
          </div>
        ) : null}
        {hasEstBonus && subTab === 'est' ? <EstBonus clinic={activeClinic} /> : <ManagerEntry clinic={activeClinic} />}
      </div>
    );
  }

  return (
    <div class="main">
      <div class="page-header">
        <div class="section-head-row">
          <h1>Payroll Dashboard</h1>
          <div class="section-head-actions">
            <FavoriteStar itemKey="payroll.dashboard" />
          </div>
        </div>
      </div>

      {/* Owner, 2026-08-17: the clinic tabs live under the page title, above
          Dashboard / Manager entry, so both sub-tabs are about the same clinic.
          A manager never sees them — they ARE the location. */}
      <ClinicTabs
        clinics={clinics}
        active={activeClinic}
        onSelect={(code) => setSelectedClinic(code)}
        management={false}
      />

      {(
        <>
      <div class="cadence-tabs" role="tablist" aria-label="Payroll section">
        <button
          type="button" role="tab"
          aria-selected={subTab === 'dashboard'}
          aria-current={subTab === 'dashboard' ? 'page' : undefined}
          class={subTab === 'dashboard' ? 'tab active' : 'tab'}
          onClick={() => setSubTab('dashboard')}
          data-testid="payroll-subnav-dashboard"
        >
          Dashboard
        </button>
        <button
          type="button" role="tab"
          aria-selected={subTab === 'entry'}
          aria-current={subTab === 'entry' ? 'page' : undefined}
          class={subTab === 'entry' ? 'tab active' : 'tab'}
          onClick={() => setSubTab('entry')}
          data-testid="payroll-subnav-entry"
        >
          Manager entry
        </button>
        {estTab}
      </div>

      {subTab === 'est' && hasEstBonus ? (
        <EstBonus clinic={activeClinic} />
      ) : subTab === 'entry' || subTab === 'est' ? (
        <ManagerEntry clinic={activeClinic} />
      ) : (
        <PayrollDashboard
          clinics={clinics}
          activeClinic={activeClinic}
          selectedDocId={selectedDocId}
          setSelectedDocId={setSelectedDocId}
        />
      )}
        </>
      )}
    </div>
  );
}

/** The Dashboard sub-item: the reviewed payroll page for the selected
 * location and week, and nothing else (owner ruling, 2026-09-07). Split out
 * of PayrollPage so a manager's early return above never mounts any of
 * this — none of these hooks run for a role that cannot reach them. */
function PayrollDashboard(
  { clinics, activeClinic, selectedDocId, setSelectedDocId }:
  {
    clinics: LocationCode[];
    activeClinic: LocationCode;
    selectedDocId: number | null;
    setSelectedDocId: (id: number) => void;
  },
): JSX.Element {
  const docs = useResource<{ documents: PayrollReviewDocumentSummary[] }>(
    REVIEW_DOCUMENTS_KEY,
    () => authedGet('/api/v1/payroll/review-documents'),
  );
  const documents = docs.status === 'ready' ? docs.data.documents : [];

  // 062: the server now sends up to six documents per week (an all-locations
  // PAGE, four per-location PAGEs, and the archived BOARD), so the week
  // shelf must list each WEEK once, never once per document. One document
  // represents the week in the dropdown and carries its identity (year,
  // adpWeek); prefer that week's all-locations PAGE for it (it is what every
  // week has always had), falling back to whatever document exists first —
  // `documents` arrives newest-week-first from the server, so weeks come out
  // in the same order here.
  const weekDocs = useMemo(() => {
    const order: string[] = [];
    const byWeek = new Map<string, PayrollReviewDocumentSummary[]>();
    for (const d of documents) {
      const key = `${d.year}-${d.adpWeek}`;
      if (!byWeek.has(key)) { byWeek.set(key, []); order.push(key); }
      byWeek.get(key)!.push(d);
    }
    return order.map((key) => {
      const forWeek = byWeek.get(key)!;
      return forWeek.find((d) => d.kind === 'PAGE' && d.locationCode === null)
        ?? forWeek.find((d) => d.kind !== 'VALIDATION' && d.kind !== 'COMPARE')
        ?? forWeek[0];
    });
  }, [documents]);

  // Default = the first (server sends newest first) — "the previous payroll
  // that was submitted" per the owner's instruction.
  const activeDocId = selectedDocId ?? weekDocs[0]?.id ?? null;
  const activeWeekDoc = weekDocs.find((d) => d.id === activeDocId) ?? null;

  // The week-picker's own label (bug found 2026-09-08): `weekDocs` above
  // picks the all-locations PAGE purely for a stable per-week identity, so
  // its dates are the shared ones even on the Lake Mary tab — a manager
  // looking at Lake Mary saw "Aug 23 – Sep 5" in the selector while the
  // page underneath (rendered from Lake Mary's own document) already said
  // Aug 24 – Sep 6. The label instead uses THIS clinic's own document for
  // the week, when one exists, falling back to the identity doc otherwise —
  // same fallback `clinicDoc`/`allLocationDoc` below already use.
  const weekDocLabel = (weekDoc: PayrollReviewDocumentSummary): string => {
    const clinicSpecific = documents.find(
      (d) => d.year === weekDoc.year && d.adpWeek === weekDoc.adpWeek
        && d.kind === 'PAGE' && d.locationCode === activeClinic,
    );
    return documentWeekLabel(clinicSpecific ?? weekDoc);
  };

  // Every document (across all locations/kinds) for the selected week —
  // used to pick the one actual document to display below, per the clinic
  // tab selected above the page.
  const docsForActiveWeek = useMemo(
    () => (activeWeekDoc
      ? documents.filter((d) => d.year === activeWeekDoc.year && d.adpWeek === activeWeekDoc.adpWeek)
      : []),
    [documents, activeWeekDoc],
  );
  // Tomer's ruling, 2026-09-07: each location has its own page. Show the
  // selected clinic's own page; if that clinic has no page for this week yet
  // (an older week not yet re-rendered per-location), fall back to the
  // all-locations page for the week — never a blank archive.
  const clinicDoc = docsForActiveWeek.find((d) => d.kind === 'PAGE' && d.locationCode === activeClinic) ?? null;
  const allLocationDoc = docsForActiveWeek.find((d) => d.kind === 'PAGE' && d.locationCode === null) ?? null;
  const boardDoc = docsForActiveWeek.find((d) => d.kind === 'BOARD') ?? null;
  // One validation report per clinic (owner, 2026-09-24) — the selected
  // clinic's own, never another clinic's.
  const validationDoc = docsForActiveWeek.find((d) => d.kind === 'VALIDATION' && d.locationCode === activeClinic) ?? null;
  // "Validation report" swaps the week's page for that week's validation
  // report (owner, 2026-09-24). Keyed to the week, so picking another week
  // goes back to its page without an effect to reset it.
  const [validationForDocId, setValidationForDocId] = useState<number | null>(null);
  const showingValidation = validationDoc !== null && validationForDocId === activeDocId;
  // "Compare to last payroll" (owner, 2026-10-20): the clinic's payroll-to-
  // payroll comparison, swapped in the same way as the validation report.
  const compareDoc = docsForActiveWeek.find((d) => d.kind === 'COMPARE' && d.locationCode === activeClinic) ?? null;
  const [compareForDocId, setCompareForDocId] = useState<number | null>(null);
  const showingCompare = !showingValidation && compareDoc !== null && compareForDocId === activeDocId;
  const activeDoc = clinicDoc ?? allLocationDoc ?? activeWeekDoc;

  // Owner ruling 2026-09-07 ("the payroll dashboard needs to go into the
  // dashboard link, period"): Dashboard renders the native week view
  // (package_view.py's own layout, reproduced from Neon rows) for the
  // selected document's ADP week, filtered to the location tab above. A week
  // with nothing loaded into `pay_period` (a history week not yet migrated,
  // or an older doc) 404s here and falls back to the archived HTML below,
  // unchanged — that fallback is the ONLY case the iframe branch still
  // serves now that the separate "Week" sub-tab is gone.
  const nativeKey = activeWeekDoc ? `payroll-week-${activeWeekDoc.adpWeek}` : null;
  const native = useResource<PayrollWeekResponse>(
    nativeKey,
    () => authedGet(`/api/v1/payroll/week/${activeWeekDoc!.adpWeek}`),
  );
  const nativeFailed = native.status === 'fault';
  // "Open the reviewed board" (clicked from inside the native view) forces
  // the archived board for THIS SAME week, without touching the week
  // selector. Comparing against activeDocId (rather than an effect that
  // resets it) means picking a different week clears the override for free.
  const [archiveOverrideId, setArchiveOverrideId] = useState<number | null>(null);
  // Tomer, 2026-09-06: the page the site shows is the approved rendered page
  // (PAYROLL_Week<N>.html, loaded as the week's document) — the native
  // component does not yet match it. The API stays live for every other
  // reader of the rows; the native view returns when it matches the page.
  const NATIVE_WEEK_VIEW = false;
  const overridden = archiveOverrideId === activeDocId;
  const showArchive = !NATIVE_WEEK_VIEW || nativeFailed || overridden || showingValidation || showingCompare;
  // The override means "show me the archived board", not "show me whichever
  // page was already selected" — everywhere else showArchive shows the
  // clinic/all-locations PAGE picked above.
  const documentToShow = showingValidation ? validationDoc : showingCompare ? compareDoc : overridden ? (boardDoc ?? activeDoc) : activeDoc;

  // Hook called unconditionally (rules of hooks) — `key: null` until the
  // archived HTML is actually the thing to show skips the fetch entirely.
  const detailKey = showArchive && documentToShow ? `payroll-review-document-${documentToShow.id}` : null;
  const detail = useResource<{ document: PayrollReviewDocumentDetail }>(
    detailKey,
    () => authedGet(`/api/v1/payroll/review-documents/${documentToShow!.id}`),
  );

  // R-5 / INV-9: a bootstrap-level fault on the `payroll` slice (an
  // admin-only refusal, or any other load failure) gates the WHOLE Dashboard
  // body — it must never render next to a leaked archive, and it must never
  // read as the same thing as "the archive is simply empty" (docs.status
  // below handles that distinct, non-fault case).
  const snap = snapshot.value;
  const payrollFault = snap.faults.find((f) => f.slice === 'payroll');
  const isAdminOnlyRefusal = !!payrollFault && /admin-only/i.test(payrollFault.detail);

  if (isAdminOnlyRefusal) {
    return (
      <div class="fault-state" role="alert" data-testid="payroll-admin-only">
        <div>Payroll archive — admin only</div>
        <div>{payrollFault!.detail}</div>
      </div>
    );
  }
  if (payrollFault) {
    return <FaultState name="payroll data did not load" detail={payrollFault.detail} />;
  }

  return (
    <div>
      {/* --- the archived weekly review document ---------------------------- */}
      {docs.status === 'loading' ? <EmptyState label="Loading the payroll archive…" /> : null}
      {docs.status === 'fault' ? (
        <FaultState name="the payroll archive did not load" detail={docs.error} />
      ) : null}
      {docs.status === 'ready' && weekDocs.length === 0 ? (
        <EmptyState label="No payroll weeks are in the archive yet." />
      ) : null}
      {docs.status === 'ready' && weekDocs.length > 0 ? (
        <div>
          <div class="money-add">
            <label>
              Payroll week:{' '}
              <select
                aria-label="Payroll week"
                value={activeDocId ?? ''}
                onChange={(e: Event) => setSelectedDocId(Number((e.target as HTMLSelectElement).value))}
              >
                {weekDocs.map((d) => (
                  <option key={d.id} value={d.id}>{weekDocLabel(d)}</option>
                ))}
              </select>
            </label>
            {validationDoc ? (
              <button
                type="button"
                class="btn"
                data-testid="payroll-validation-toggle"
                onClick={() => { setCompareForDocId(null); setValidationForDocId(showingValidation ? null : activeDocId); }}
              >
                {showingValidation ? "Back to the week's page" : 'Validation report'}
              </button>
            ) : null}
            {compareDoc ? (
              <button
                type="button"
                class="btn"
                data-testid="payroll-compare-toggle"
                onClick={() => { setValidationForDocId(null); setCompareForDocId(showingCompare ? null : activeDocId); }}
              >
                {showingCompare ? "Back to the week's page" : 'Compare to last payroll'}
              </button>
            ) : null}
          </div>
          {native.status === 'loading' ? <EmptyState label="Loading that week's numbers…" /> : null}
          {native.status === 'ready' && !showArchive ? (
            <PayrollWeekView
              data={native.data}
              activeClinic={activeClinic}
              matchingDoc={boardDoc}
              onOpenDocument={() => setArchiveOverrideId(activeDocId)}
            />
          ) : null}
          {showArchive ? (
            <>
              {detail.status === 'loading' ? <EmptyState label="Loading that week's numbers…" /> : null}
              {detail.status === 'fault' ? (
                <FaultState name="that week's numbers did not load" detail={detail.error} />
              ) : null}
              {detail.status === 'ready' ? (
                // The document is the owner's own generated review, stored in
                // this database and served admin-only; it is trusted content,
                // not user input. `allow-scripts` alone (an opaque origin)
                // rendered blank in the real browser on 2026-08-15; with
                // `allow-same-origin` it renders. Its own tab script needs to
                // run for the four clinic panes to switch, so it stays a
                // frame rather than inlined HTML.
                <iframe
                  sandbox="allow-scripts allow-same-origin"
                  srcDoc={detail.data.document.html}
                  title={documentToShow ? documentWeekLabel(documentToShow) : 'Payroll week'}
                  class="archive-frame"
                  style={{ width: '100%', height: '80vh', border: 0 }}
                  scrolling="no"
                  onLoad={(ev: Event) => {
                    // Tomer, 2026-09-07: the page scrolls as one page, not a
                    // scrollable box inside it — size the frame to its content.
                    const f = ev.currentTarget as HTMLIFrameElement;
                    const h = f.contentDocument?.documentElement?.scrollHeight;
                    if (h) f.style.height = `${h + 24}px`;
                  }}
                />
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
